import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from "../";
import { IBaseResponse, ICenterBase, IListItem } from '@nkr/models/index';
import { IPost } from '../authorization';
import { IMicrositeCampaign, IMicrositeCampaignListing, IMicrositeCampaignValidatedMailingList } from '@nkr/models/microsites';
import { IDelete, IGet, IPut } from '../authorization/actions';
import { MicrositeType } from '@nkr/enums/index';

export interface IMicrositeCampaignListResponse {
    data: {
        success: boolean,
        message: string,
        results: IMicrositeCampaignListing[]
    }
}

export interface IMicrositeCampaignResponse {
    data: {
        success: boolean,
        message: string,
        results: IMicrositeCampaignListing
    }
}

export interface IMicrositeCampaignValidatedMailingListResponse {
    data: {
        success: boolean,
        message: string,
        results: IMicrositeCampaignValidatedMailingList[]
    }
}

export interface IMicrositeCampaignLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            centerList: ICenterBase[],
            micrositeTypeList: IListItem[]
        }
    }
}

export const ActionCreators = {
    getMicrositeCampaignList: (centerId: number, micrositeType?: MicrositeType) => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignListResponse> => {
        let options = {
            url: `${__API__}/microsite/campaign?centerId=${centerId}` + (micrositeType != null ? `&micrositeType=${micrositeType}` : '')
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getCampaign: (id: number, fileContents?: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignResponse> => {
        let options = {
            url: `${__API__}/microsite/campaign/${id}?fileContents=${fileContents}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getValidatedCampaignMailingList: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignValidatedMailingListResponse> => {
        let options = {
            url: `${__API__}/microsite/campaign/${id}/validated-mailing-list`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getCampaignLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignLookupDataResponse> => {
        let options = {
            url: `${__API__}/microsite/campaign/lookup-data`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    saveCampaign: (id: number, centerId: number, selfRegistrationKey: string, campaignName: string, micrositeTypeId: number, startDate: string, endDate: string, campaignTypeId: number, processedByNkr: boolean, letter: any) => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignResponse> => {
        let data = {
            id,
            centerId,
            selfRegistrationKey,
            campaignName,
            micrositeTypeId,
            startDate,
            endDate,
            campaignTypeId,
            processedByNkr,
            letter
        };

        let options: IPost = {
            url: `${__API__}/microsite/campaign`,
            data
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    deleteCampaign: (campaignId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: `${__API__}/microsite/campaign/${campaignId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiDelete(options));
        } catch (error) {
            throw error;
        }
    },
    updateCampaignLetter: (id: number, salutation: string, intro: string, closing: string, signature: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IMicrositeCampaignResponse> => {
        let data = {
            salutation,
            intro,
            closing,
            signature
        };

        let options: IPut = {
            url: `${__API__}/microsite/campaign/${id}/letter`,
            data
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error_1) {
            throw error_1;
        }
    },
    uploadCampaignMailingList: (id: number, file: File) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('file', file);

        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/mailing-list`,
            data: formData
        };

        try {
            return await dispatch(AuthActionCreators.apiPostFormData(config));
        } catch (error) {
            throw error;
        }
    },
    saveCampaignMailingListMapping: (id: number, hasColumnHeaders: boolean, columns: string[]) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/mailing-list-mapping`,
            data: {
                hasColumnHeaders,
                columns
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(config));
        } catch (error) {
            throw error;
        }
    },
    confirmMailingList: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/confirm-mailing-list`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(config));
        } catch (error) {
            throw error;
        }
    },
    uploadSignatureFile: (id: number, file: File) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('file', file);

        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/upload-signature-file`,
            data: formData
        };

        try {
            return await dispatch(AuthActionCreators.apiPostFormData(config));
        } catch (error) {
            throw error;
        }
    },
    saveCampaignLetterContents: (id: number, salutation: string, intro: string, closing: string, signature: string, submitToNkr: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/letter-contents`,
            data: {
                salutation,
                intro,
                closing,
                signature,
                submitToNkr
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(config));
        } catch (error) {
            throw error;
        }
    },
    previewCampaignLetter: (id: number, salutation: string, intro: string, closing: string, signature: string) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/preview-campaign-letter`,
            data: {
                salutation,
                intro,
                closing,
                signature
            },
            responseType: 'blob'
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(config));
        } catch (error) {
            throw error;
        }
    },
    orderCampaignBrochures: (id: number, orderCount: number, addressId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/microsite/campaign/${id}/order-brochures`,
            data: {
                orderCount,
                addressId
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(config));
        } catch (error) {
            throw error;
        }
    },

    downloadCampaignPdf: (campaignId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options: IGet = {
            url: `${__API__}/microsite/campaign/${campaignId}/pdf`,
            responseType: 'blob'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    downloadMailingList: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/microsite/campaign/${id}/download-mailing-list`,
            responseType: 'blob'
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
}